import { useState } from "react";
import { useForm } from "@mantine/form";
import { zodResolver } from "mantine-form-zod-resolver";
import {
  Alert,
  Box,
  Button,
  Divider,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
  useComputedColorScheme,
} from "@mantine/core";
import { IconAlertCircle, IconFileText } from "@tabler/icons-react";
import { LoginRequest } from "@iso27001/shared";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext.js";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useApiErrorMessage } from "../i18n/api-error.js";
import classes from "./LoginPage.module.css";

export function LoginPage() {
  const { login, sessionExpired } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const scheme = useComputedColorScheme("light");
  const apiError = useApiErrorMessage();
  const { t } = useTranslation();
  const [err, setErr] = useState<string | null>(null);
  const form = useForm<{ email: string; password: string }>({
    initialValues: { email: "", password: "" },
    validate: zodResolver(LoginRequest),
  });

  const onSubmit = form.onSubmit(async (vals) => {
    setErr(null);
    try {
      await login(vals);
      const target = (loc.state as { from?: { pathname: string } } | undefined)?.from?.pathname ?? "/";
      nav(target, { replace: true });
    } catch (e) {
      // The login form is the one place a 401 really does mean "those
      // credentials are wrong". Everywhere else it means the session went
      // away mid-session, which is what the shared errors.Unauthorized text
      // now says — telling someone their password is wrong over a half-filled
      // risk form is both false and unhelpful.
      setErr(apiError(e, { Unauthorized: "auth.login.error.invalidCredentials" }));
    }
  });

  return (
    <Box className={classes.screen}>
      <Stack className={classes.content} gap="xl">
        {/*
          Brand lockup. The mark is the real asset; the wordmark is set as text
          rather than approximated, so nothing here is a redrawn logo. Swapping
          in the supplied horizontal lockup means replacing this Group with a
          single <img>.
          Mirrors Header.tsx's scheme-to-asset mapping so the two never diverge.
        */}
        <Group gap="sm" className={`${classes.rise} ${classes.delay1}`}>
          <img
            src={scheme === "dark" ? "/logo-dark.png" : "/logo-light.png"}
            alt="axxalon"
            className={classes.mark}
          />
          <Text component="span" className={classes.wordmark}>
            axxalon
          </Text>
        </Group>

        <Stack gap={6} className={`${classes.rise} ${classes.delay2}`}>
          <Text component="span" className={classes.kicker}>
            {t("auth.hero.kicker")}
          </Text>
          <Text component="h1" className={classes.title}>
            {t("auth.hero.title")}
          </Text>
          <Text size="sm" c="dimmed">
            {t("auth.hero.subtitle")}
          </Text>
        </Stack>

        <Paper
          withBorder
          shadow="md"
          p="xl"
          radius="md"
          className={`${classes.card} ${classes.rise} ${classes.delay3}`}
        >
          <Stack>
            <Title order={4}>{t("auth.login.title")}</Title>
            {/*
              Landing on a login screen with no explanation reads as a bug, and
              with a 20-minute idle timeout this happens to people several times
              a day. Suppressed once they have actually tried to sign in: at
              that point the red error below is the more useful message, and two
              stacked alerts about the same login is noise.
            */}
            {sessionExpired && !err && (
              <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
                {t("auth.login.sessionExpired")}
              </Alert>
            )}
            {err && <Alert color="red" icon={<IconAlertCircle size={16} />}>{err}</Alert>}
            <form onSubmit={onSubmit}>
              <Stack>
                <TextInput label={t("auth.login.email")} autoFocus required {...form.getInputProps("email")} />
                <PasswordInput label={t("auth.login.password")} required {...form.getInputProps("password")} />
                <Button type="submit" loading={form.submitting}>{t("auth.login.submit")}</Button>
              </Stack>
            </form>
            {/*
              The öffentlich documents are readable without a session, but until
              now the only way in was to type /dokumentation by hand — the login
              page was a dead end for anyone who isn't a user. This mirrors the
              "Anmelden" button that PublicDocsLayout shows anonymous readers, so
              the two entry points point at each other.
            */}
            <Divider label={t("auth.login.or")} labelPosition="center" />
            <Button
              component={Link}
              to="/dokumentation"
              variant="light"
              leftSection={<IconFileText size={16} />}
            >
              {t("auth.login.publicDocs")}
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
